import { makeStyles, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useSelector } from 'react-redux';
import { ReactComponent as NoteIcon } from '../../../../../assets/icons/note.svg';
import AccountListItem from '../../../../Shared/AccountListItem/AccountListItem';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  header: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    flexShrink: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors?.grey || '#8E8E8E',
    marginTop: theme.spacing(0.5),
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 3,
      '&:hover': {
        background: 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.colors?.grey || '#8E8E8E',
  },
  emptyIcon: {
    width: 'auto',
    height: 48,
    opacity: 0.3,
    marginBottom: theme.spacing(2),
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  emptyText: {
    fontSize: 14,
    color: theme.colors?.grey || '#8E8E8E',
  },
  removeButton: {
    color: theme.colors?.grey || '#8E8E8E',
    '&:hover': {
      color: theme.colors?.darkError || '#dc1212',
      backgroundColor: 'rgba(220, 18, 18, 0.04)',
    },
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: 'rgba(0, 168, 255, 0.1)',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
}));

const SelectedAccountsList = ({
  selectedAccounts = [],
  onRemoveAccount,
  maxHeight = '500px',
}) => {
  const classes = useStyles();
  const {
    en: { titles },
  } = useSelector(state => state.i18n);

  return (
    <div className={classes.container} style={{ maxHeight }}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {titles.selectedAccounts || 'Selected Accounts'}
        </Typography>
        <Typography className={classes.subtitle}>
          {selectedAccounts.length}{' '}
          {selectedAccounts.length === 1 ? 'account' : 'accounts'} selected
        </Typography>
      </div>

      <div className={classes.listContainer}>
        {selectedAccounts.length === 0 ? (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>
              <NoteIcon />
            </div>
            <Typography className={classes.emptyText}>
              {titles.noAccountsSelected || 'No accounts selected yet'}
            </Typography>
            <Typography className={classes.emptyText} style={{ marginTop: 8 }}>
              {titles.selectAccountsFromTable ||
                'Select accounts from the table on the left'}
            </Typography>
          </div>
        ) : (
          <>
            {selectedAccounts.length > 5 && (
              <div className={classes.badge}>
                {titles.multipleSelection || 'Multiple Selection'}
              </div>
            )}
            {selectedAccounts.map((account, index) => (
              <AccountListItem
                key={account.uuid || account.businessName || index}
                businessName={account.businessName}
                customIdentifier={account.customIdentifier}
                actionIcon={<CloseIcon fontSize="small" />}
                onAction={() => onRemoveAccount(account)}
                actionTooltip={
                  titles.removeFromSelection || 'Remove from selection'
                }
                actionClassName={classes.removeButton}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default SelectedAccountsList;
